import { Column } from 'primereact/column'
import { TreeNode } from 'primereact/treenode'
import { TreeTable, TreeTableExpandedKeysType, TreeTableSelectionEvent, TreeTableSelectionKeysType } from 'primereact/treetable'
import groupBy from 'lodash-es/groupBy'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Panel } from 'primereact/panel'
import { Product_Output } from '@/api'

export interface ItemStats {
  '総注文数': number
  '総成形数': number
}

export interface ItemTreeTableProps {
  products: Product_Output[]
  itemStatus: Record<Product_Output['sku_id'], Set<'empty' | 'little' | 'over'>>
  itemStats: Record<Product_Output['sku_id'], ItemStats>
  onSelectionChange?: (products: Product_Output[], isCategory: boolean) => void
}

interface FilterCheckboxes {
  statusMinus: boolean
  statusOver: boolean
  statusLittle: boolean
}

const baseStats: ItemStats = {
  総注文数: 0,
  総成形数: 0,
}

const flattenProducts = (list: Product_Output[]): Product_Output[] => {
  const result: Product_Output[] = []
  const walk = (product: Product_Output) => {
    result.push(product)
    ;(product.components ?? []).forEach(walk)
  }
  list.forEach(walk)
  return result
}

const collectKeys = (nodes: TreeNode[]): string[] => {
  const keys: string[] = []
  nodes.forEach((node) => {
    if (node.key) {
      keys.push(String(node.key))
    }
    if (node.children?.length) {
      keys.push(...collectKeys(node.children))
    }
  })
  return keys
}

const collectExpandableKeys = (nodes: TreeNode[]): string[] => {
  const keys: string[] = []
  nodes.forEach((node) => {
    if (node.children?.length) {
      if (node.key) {
        keys.push(String(node.key))
      }
      keys.push(...collectExpandableKeys(node.children))
    }
  })
  return keys
}

const collectProductIdsFromNodes = (nodes: TreeNode[]): string[] => {
  const ids: string[] = []
  nodes.forEach((node) => {
    const id = (node.data as { 品目ID?: string } | undefined)?.品目ID
    if (id) {
      ids.push(id)
    }
    if (node.children?.length) {
      ids.push(...collectProductIdsFromNodes(node.children))
    }
  })
  return ids
}

const collectProductKeys = (node: TreeNode): string[] => {
  const keys: string[] = []
  if (node.key && String(node.key).startsWith('product-')) {
    keys.push(String(node.key))
  }
  node.children?.forEach((child) => {
    keys.push(...collectProductKeys(child))
  })
  return keys
}

/**
 * 品目マスタをツリーテーブルで表示するコンポーネント
 */
const ItemTreeTable = ({ products, itemStatus, itemStats, onSelectionChange }: ItemTreeTableProps) => {
  const [selection, setSelection] = useState<TreeTableSelectionKeysType>({})
  const [expandedKeys, setExpandedKeys] = useState<TreeTableExpandedKeysType>({})
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [filterCheckboxes] = useState<FilterCheckboxes>({
    statusMinus: false,
    statusOver: false,
    statusLittle: false,
  })

  const flatProducts = useMemo(() => flattenProducts(products), [products])

  const filterKeyword = useCallback(
    (product: Product_Output) => {
      if (searchKeyword === '') return true

      const searchKeyword_N = searchKeyword.normalize('NFKC')
      const name_N = String(product.name ?? '').normalize('NFKC')
      const category_N = String(product.category ?? '').normalize('NFKC')
      const machine_N = String(product.machine_sheet ?? '').normalize('NFKC')
      return (
        String(product.sku_id).includes(searchKeyword_N) ||
        name_N.includes(searchKeyword_N) ||
        category_N.includes(searchKeyword_N) ||
        machine_N.includes(searchKeyword_N)
      )
    },
    [searchKeyword],
  )

  const productFilter = useCallback(
    (product: Product_Output) => {
      const status = itemStatus[product.sku_id]
      if (filterCheckboxes.statusMinus && !(status?.has('empty'))) return false
      if (filterCheckboxes.statusLittle && !(status?.has('little'))) return false
      if (filterCheckboxes.statusOver && !(status?.has('over'))) return false
      return filterKeyword(product)
    },
    [filterCheckboxes, filterKeyword, itemStatus],
  )

  const formatNumber = (num: number | undefined): string => {
    if (num === undefined) return '-'
    return num.toLocaleString('en-US')
  }

  const treeNodes = useMemo((): TreeNode[] => {
    const buildProductNode = (product: Product_Output): TreeNode | null => {
      const children = (product.components ?? [])
        .map((child) => buildProductNode(child))
        .filter(Boolean) as TreeNode[]

      const matched = productFilter(product)
      if (!matched && children.length === 0) {
        return null
      }

      const stats = itemStats[product.sku_id] ?? baseStats
      const rowIndex = product.row_index ?? null
      const hasStockout = itemStatus[product.sku_id]?.has('empty') ?? false
      const className = hasStockout ? 'font-bold text-red-600' : ''

      return {
        key: `product-${product.sku_id}`,
        data: {
          品目ID: product.sku_id,
          品名: product.category,
          総成形数: formatNumber(stats.総成形数),
          総注文数: formatNumber(stats.総注文数),
          行: rowIndex,
        },
        leaf: children.length === 0,
        selectable: true,
        children,
        className,
      } satisfies TreeNode
    }

    const categories = groupBy(products, (product) => {
      const machine = (product.machine_sheet ?? '').trim()
      return machine || '未指定'
    }) as Record<string, Product_Output[]>

    return Object.keys(categories)
      .map((category): TreeNode | null => {
        const categoryChildren = categories[category]
          .map((product) => buildProductNode(product))
          .filter(Boolean) as TreeNode[]

        if (!categoryChildren.length) {
          return null
        }

        const productIds = collectProductIdsFromNodes(categoryChildren)
        const categoryStats = productIds.reduce<ItemStats>((acc, id) => {
          const stats = itemStats[id] ?? baseStats
          acc.総成形数 += stats.総成形数
          acc.総注文数 += stats.総注文数
          return acc
        }, { ...baseStats })

        // カテゴリ内に在庫マイナスがある場合は強調
        const hasStockoutInCategory = productIds.some((id) => itemStatus[id]?.has('empty'))
        const className = hasStockoutInCategory ? 'font-bold text-red-600' : ''

        return {
          key: `category-${category}`,
          data: {
            category: `${category}(${productIds.length})`,
            行: null,
            総成形数: formatNumber(categoryStats.総成形数),
            総注文数: formatNumber(categoryStats.総注文数),
          },
          leaf: false,
          selectable: true,
          children: categoryChildren,
          className,
        } satisfies TreeNode
      })
      .filter((node): node is TreeNode => node !== null) as TreeNode[]
  }, [products, productFilter, itemStats, itemStatus])

  // 現在のツリー構造に存在しないキーを選択状態から除外し、外部の選択状態も同期する
  useEffect(() => {
    const validKeys = new Set<string>(collectKeys(treeNodes))

    const nextSelection: TreeTableSelectionKeysType = {}
    let changed = false
    Object.keys(selection || {}).forEach((key) => {
      if (validKeys.has(key)) {
        nextSelection[key] = (selection as TreeTableSelectionKeysType)[key]
      } else {
        changed = true
      }
    })

    if (changed) {
      setSelection(nextSelection)
      if (onSelectionChange) {
        const selectedProducts = flatProducts.filter((product) => nextSelection[`product-${product.sku_id}`])
        onSelectionChange(selectedProducts, false)
      }
    }
  }, [treeNodes, selection, onSelectionChange, flatProducts])

  const handleSelectionChange = (e: TreeTableSelectionEvent) => {
    const selkeys = { ...(e.value as TreeTableSelectionKeysType) }
    let isCategory = false

    Object.keys(selkeys).forEach((key) => {
      if (key.startsWith('category-') && selkeys[key]) {
        const categoryNode = treeNodes.find((node) => node.key === key)
        if (categoryNode) {
          collectProductKeys(categoryNode).forEach((productKey) => {
            selkeys[productKey] = true
          })
        }
        isCategory = true
      }
    })

    setSelection(selkeys as TreeTableSelectionKeysType)

    if (onSelectionChange) {
      const selectedProducts = flatProducts.filter((product) => selkeys[`product-${product.sku_id}`])
      onSelectionChange(selectedProducts, isCategory)
    }
  }

  const handleAllExpand = (expand: boolean) => () => {
    if (expand) {
      const keys = collectExpandableKeys(treeNodes)
      const expanded = keys.reduce<TreeTableExpandedKeysType>((acc, key) => {
        acc[key as keyof TreeTableExpandedKeysType] = true
        return acc
      }, {})
      setExpandedKeys(expanded)
    } else {
      setExpandedKeys({})
    }
  }

  const handleAllSelection = (selected: boolean) => () => {
    if (selected) {
      setSelection({})
      if (onSelectionChange) {
        onSelectionChange([], false)
      }
    }
  }

  const selectionCount = Object.keys(selection).length

  return (
    <Panel toggleable header="分類一覧" className="w-full">
      <div className="flex flex-column gap-2">
        <div className="flex align-items-center justify-start gap-2">
          <Button icon="pi pi-minus" size="small" tooltip="全て閉じる" tooltipOptions={{ position: 'top' }} onClick={handleAllExpand(false)} />
          <Button icon="pi pi-plus" size="small" tooltip="全て開く" tooltipOptions={{ position: 'top' }} onClick={handleAllExpand(true)} />
          <Button label={selection ? `選択中: ${selectionCount}件を解除` : '-'} size="small" disabled={selectionCount === 0} severity="info" onClick={handleAllSelection(true)} />
        </div>
        <div className="flex flex-column gap-2">
          <div className="p-inputgroup">
            <span className="p-inputgroup-addon text-nowrap" style={{ width: 100 }}>検索</span>
            <InputText id="search" size="small" className="w-full" onChange={(e) => setSearchKeyword(e.target.value)} />
          </div>
        </div>

        <TreeTable
          paginator
          rows={25}
          rowsPerPageOptions={[10, 25, 50]}
          value={treeNodes}
          tableStyle={{ width: '100%' }}
          selectionKeys={selection}
          selectionMode="multiple"
          metaKeySelection={true}
          onSelectionChange={handleSelectionChange}
          columnResizeMode="expand"
          expandedKeys={expandedKeys}
          onToggle={(e) => setExpandedKeys(e.value)}
        >
          <Column
            field="category"
            header=""
            className="col-1"
            expander
            style={{
              overflow: 'visible',
              textWrap: 'nowrap',
            }}
          />
          <Column field="品目ID" header="ID" className="col-2" sortable />
          <Column field="品名" header="品名" className="col-4" sortable />
          <Column field="総注文数" header="総注文数" className="col-1" sortable />
          <Column field="総成形数" header="総成形数" className="col-1" sortable />
          <Column field="行" header="行" className="col-1" sortable headerTooltip="Excelファイル内の行番号" />
        </TreeTable>
      </div>
    </Panel>
  )
}

export default ItemTreeTable
